import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { X } from "lucide-react";
import { cn } from "./utils";
import { 
  STATUS_MAP, 
  type StatusType 
} from "./status";

const badgeVariants = cva(
  "inline-flex items-center justify-center whitespace-nowrap font-semibold rounded-[999px] duration-normal focus:outline-none focus:ring-2 focus:ring-Color-Background-brand-default/20 focus:ring-offset-2",
  {
    variants: {
      variant: {
        filled: "",
        outlined: "border bg-transparent",
        subtle: "",
      },
      badgeColor: {
        green: "",
        blue: "",
        yellow: "",
        orange: "",
        red: "",
        purple: "",
        teal: "",
        violet: "",
        gray: "",
      },
      size: {
        sm: "h-[18px] px-3 gap-2.5 text-[10px] leading-4 font-['Be_Vietnam_Pro']",
        lg: "h-[30px] px-3 gap-1.5 text-xs",
      },
    },
    compoundVariants: [
      {
        variant: "filled",
        badgeColor: "green",
        class:
          "bg-Color-Background-positive-default text-Color-Text-neutral-white",
      },
      {
        variant: "outlined",
        badgeColor: "green",
        class: "border-Color-Stroke-positive text-Color-Text-semantic-positive",
      },
      {
        variant: "subtle",
        badgeColor: "green",
        class:
          "bg-Color-Background-positive-inverted text-Color-Text-semantic-positive",
      },
      {
        variant: "filled",
        badgeColor: "blue",
        class:
          "bg-Color-Background-others-blue-default text-Color-Text-neutral-white",
      },
      {
        variant: "outlined",
        badgeColor: "blue",
        class: "border-Color-Stroke-others-blue text-Color-Text-others-blue",
      },
      {
        variant: "subtle",
        badgeColor: "blue",
        class:
          "bg-Color-Background-others-blue-inverted text-Color-Text-others-blue",
      },
      {
        variant: "filled",
        badgeColor: "teal",
        class:
          "bg-Color-Background-others-teal-default text-Color-Text-neutral-white",
      },
      {
        variant: "outlined",
        badgeColor: "teal",
        class: "border-Color-Stroke-others-teal text-Color-Text-others-teal",
      },
      {
        variant: "subtle",
        badgeColor: "teal",
        class:
          "bg-Color-Background-others-teal-inverted text-Color-Text-others-teal",
      },
      {
        variant: "filled",
        badgeColor: "yellow",
        class:
          "bg-Color-Background-brand-default text-Color-Text-neutral-white",
      },
      {
        variant: "outlined",
        badgeColor: "yellow",
        class: "border-Color-Stroke-brand text-Color-Text-brand-default",
      },
      {
        variant: "subtle",
        badgeColor: "yellow",
        class:
          "bg-Color-Background-brand-inverted text-Color-Text-brand-default",
      },
      {
        variant: "filled",
        badgeColor: "orange",
        class:
          "bg-Color-Background-warning-default text-Color-Text-neutral-white",
      },
      {
        variant: "outlined",
        badgeColor: "orange",
        class: "border-Color-Stroke-warning text-Color-Text-semantic-warning",
      },
      {
        variant: "subtle",
        badgeColor: "orange",
        class:
          "bg-Color-Background-warning-inverted text-Color-Text-semantic-warning",
      },
      {
        variant: "filled",
        badgeColor: "red",
        class:
          "bg-Color-Background-negative-default text-Color-Text-neutral-white",
      },
      {
        variant: "outlined",
        badgeColor: "red",
        class: "border-Color-Stroke-negative text-Color-Text-semantic-negative",
      },
      {
        variant: "subtle",
        badgeColor: "red",
        class:
          "bg-Color-Background-negative-inverted text-Color-Text-semantic-negative",
      },
      {
        variant: "filled",
        badgeColor: "purple",
        class:
          "bg-Color-Background-others-purple-default text-Color-Text-neutral-white",
      },
      {
        variant: "outlined",
        badgeColor: "purple",
        class:
          "border-Color-Stroke-others-purple text-Color-Text-others-purple",
      },
      {
        variant: "subtle",
        badgeColor: "purple",
        class:
          "bg-Color-Background-others-purple-inverted text-Color-Text-others-purple",
      },
      {
        variant: "filled",
        badgeColor: "violet",
        class:
          "bg-Color-Background-others-violet-default text-Color-Text-neutral-white",
      },
      {
        variant: "outlined",
        badgeColor: "violet",
        class:
          "border-Color-Stroke-others-violet text-Color-Text-others-violet",
      },
      {
        variant: "subtle",
        badgeColor: "violet",
        class:
          "bg-Color-Background-others-violet-inverted text-Color-Text-others-violet",
      },
      {
        variant: "filled",
        badgeColor: "gray",
        class:
          "bg-Color-Background-neutral-pressed text-Color-Text-neutral-black",
      },
      {
        variant: "outlined",
        badgeColor: "gray",
        class: "border-Color-Stroke-neutral text-Color-Text-neutral-supplement",
      },
      {
        variant: "subtle",
        badgeColor: "gray",
        class:
          "bg-Color-Background-neutral-default_secondary text-Color-Text-neutral-supplement",
      },
    ],
    defaultVariants: {
      variant: "filled",
      badgeColor: "gray",
      size: "sm",
    },
  },
);

// Status mappings with Vietnamese labels - MEBI Specification
const STATUS_MAPPINGS = STATUS_MAP;

export interface BadgeProps
  extends
    React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof badgeVariants> {
  statusType?: StatusType;
  statusValue?: string;
  dismissible?: boolean;
  onDismiss?: () => void;
  icon?: React.ReactNode;
  iconPosition?: "left" | "right";
}

const Badge = React.forwardRef<HTMLDivElement, BadgeProps>(
  (
    {
      className,
      variant,
      badgeColor,
      size,
      statusType,
      statusValue,
      dismissible,
      onDismiss,
      icon,
      iconPosition = "right",
      children,
      ...props
    },
    ref,
  ) => {
    // Status mapping logic
    let finalVariant = variant;
    let finalColor = badgeColor;
    let displayText = children;

    if (statusType && statusValue) {
      const statusGroup = STATUS_MAPPINGS[statusType];
      if (statusGroup && statusValue in statusGroup) {
        const statusConfig = statusGroup[
          statusValue
        ]
        finalVariant = statusConfig.variant;
        finalColor = statusConfig.badgeColor;
        displayText = statusConfig.label;
      }
    } // Icon size based on badge size
    const iconSize = size === "lg" ? "h-3.5 w-3.5" : "h-3 w-3";

    // Render icon with proper size
    const renderIcon = (iconElement: React.ReactNode) => {
      if (React.isValidElement(iconElement)) {
        return React.cloneElement(
          iconElement as React.ReactElement<{ className?: string }>,
          {
            className: cn(
              iconSize,
              (iconElement.props as { className?: string }).className,
            ),
          },
        );
      }
      return iconElement;
    };

    return (
      <div
        ref={ref}
        className={cn(
          badgeVariants({
            variant: finalVariant,
            badgeColor: finalColor,
            size,
          }),
          className,
        )}
        {...props}
      >
        {/* Left icon */}
        {icon && iconPosition === "left" && renderIcon(icon)}

        {/* Text content */}
        {displayText}

        {/* Right icon */}
        {icon && iconPosition === "right" && renderIcon(icon)}

        {/* Dismiss button */}
        {dismissible && (
          <button
            onClick={onDismiss}
            className={cn(
              "rounded-full hover:bg-black/10 flex items-center justify-center",
              size === "lg" ? "ml-1.5 h-4 w-4" : "ml-1 h-3 w-3",
            )}
            aria-label="Dismiss badge"
          >
            <X className={size === "lg" ? "h-2.5 w-2.5" : "h-2 w-2"} />
          </button>
        )}
      </div>
    );
  },
);

Badge.displayName = "Badge";

export { Badge };
